import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Clock, CloseBig } from "@tiny/icons";
import { format, isValid } from "date-fns";
import React from "react";
import { Controller, FormProvider } from "react-hook-form";
import useTimerDelayForm from "./TimerDelayForm.hooks";
import DelayDateNote from "./DelayDateNote";
import { DelayMode } from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/types";
import {
  DAYS_OF_WEEK,
  formatTime12h,
  formatTimeZoneLabel,
  getMaxSpecificDate,
  getTimeZoneLabelKey,
  getZoneToday,
  toTime24h,
  zonedWallTimeToInstant,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/CustomNodes/Timing.helpers";
import { ampmOptions, hourOptions, minuteOptions } from "../../utils/dateUtils";

const DELAY_FIELDS = [
  { name: "days", label: "Days", options: 31 },
  { name: "hours", label: "Hours", options: 24 },
  { name: "minutes", label: "Minutes", options: 60 },
] as const;

const TIME_FIELDS = [
  { name: "hour", label: "Hour", options: hourOptions },
  { name: "minute", label: "Minute", options: minuteOptions },
  { name: "ampm", label: "AM/PM", options: ampmOptions },
] as const;

const DELAY_MODES: { value: DelayMode; label: string }[] = [
  { value: "RELATIVE", label: "Wait for a set amount of time" },
  { value: "TIME_OF_DAY", label: "Wait until a time of day" },
  { value: "SPECIFIC_DATE", label: "Wait until a specific date" },
  { value: "DAY_OF_WEEK", label: "Wait until a day of the week" },
];

/** "YYYY-MM-DD" <-> a local Date at midnight, for the date picker. */
const toPickerDate = (date: string | null) => {
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const fromPickerDate = (date: Date | null) =>
  date && isValid(date) ? format(date, "yyyy-MM-dd") : null;

const SelectField = ({
  name,
  label,
  value,
  onChange,
  options,
}: {
  name: string;
  label: string;
  value: string | number;
  onChange: (value: any) => void;
  options: readonly { value: string | number; label: string }[];
}) => (
  <FormControl fullWidth>
    <InputLabel
      shrink
      id={`time-delay-${name}-label`}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        pr: 2,
      })}
    >
      {label}
    </InputLabel>
    <Select
      size="small"
      fullWidth
      labelId={`time-delay-${name}-label`}
      value={value}
      onChange={(e: any) => onChange(e.target.value)}
      aria-label={name}
      label={label}
      displayEmpty
    >
      {options.map((option) => (
        <MenuItem key={`${name}-${option.value}`} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const TimeDelayForm = (props: { id: string }) => {
  const {
    goBack,
    onSubmit,
    methods,
    formValues,
    control,
    saveTimerDisabled,
    dateNote,
    timeZoneOptions,
    viewMode,
  } = useTimerDelayForm(props);
  const { mode } = formValues;

  // The picker's own value, kept even while it is an Invalid Date from a
  // half-typed entry: handing the picker null mid-typing makes it treat the
  // change as programmatic and wipe the input. The form only ever holds a
  // complete "YYYY-MM-DD" (or null).
  const [pickerDate, setPickerDate] = React.useState<Date | null>(() =>
    toPickerDate(formValues.date)
  );
  // "Today" and the 2-year limit are the chosen zone's, not the browser's.
  const minDate = toPickerDate(getZoneToday(formValues.timeZone));
  const maxDate = toPickerDate(getMaxSpecificDate(formValues.timeZone));

  // Zone abbreviations: at the chosen moment for a specific date, otherwise
  // now. The labels are rebuilt whenever that instant changes (to the minute
  // for "now"), so EST/EDT is right on a DST-change day too.
  const labelTarget =
    mode === "SPECIFIC_DATE"
      ? zonedWallTimeToInstant(
          formValues.date,
          toTime24h(formValues.hour, formValues.minute, formValues.ampm),
          formValues.timeZone
        )
      : undefined;
  const hasLabelTarget = !!labelTarget;
  const labelKey = getTimeZoneLabelKey(labelTarget);
  // A relative delay has no time-zone picker, so it builds no labels.
  const needsLabels = mode !== "RELATIVE";
  const timeZoneLabels = React.useMemo(() => {
    if (!needsLabels) return new Map<string, string>();
    const at = new Date(hasLabelTarget ? labelKey : labelKey * 60000);
    return new Map(
      timeZoneOptions.map((zone) => [zone, formatTimeZoneLabel(zone, at)])
    );
  }, [needsLabels, timeZoneOptions, labelKey, hasLabelTarget]);
  const timeZoneLabel = (zone: string) => timeZoneLabels.get(zone) ?? zone;
  const filterTimeZones = React.useMemo(
    () =>
      createFilterOptions<string>({
        stringify: (zone) => `${timeZoneLabels.get(zone) ?? ""} ${zone}`,
      }),
    [timeZoneLabels]
  );

  const time = formatTime12h(
    toTime24h(formValues.hour, formValues.minute, formValues.ampm)
  );
  const hint: Partial<Record<DelayMode, string>> = {
    TIME_OF_DAY: `Contacts continue at the next ${time} after they arrive.`,
    SPECIFIC_DATE:
      "Contacts that arrive after this date wait until the same date and time next year.",
    DAY_OF_WEEK:
      "Contacts continue at the first matching day after they arrive.",
  };

  const relativeFields = (
    <Box sx={{ display: "flex", gap: 4 }}>
      {DELAY_FIELDS.map(({ name, label, options }) => (
        <Box key={name} sx={{ flex: 1, minWidth: 0 }}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <SelectField
                name={name}
                label={label}
                value={field.value}
                onChange={field.onChange}
                options={[...new Array(options)].map((_, index) => ({
                  value: index,
                  label: String(index),
                }))}
              />
            )}
          />
        </Box>
      ))}
    </Box>
  );

  const timeFields = (
    <Box sx={{ display: "flex", gap: 4 }}>
      {TIME_FIELDS.map(({ name, label, options }) => (
        <Box key={name} sx={{ flex: 1, minWidth: 0 }}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <SelectField
                name={name}
                label={label}
                value={field.value}
                onChange={field.onChange}
                options={options}
              />
            )}
          />
        </Box>
      ))}
    </Box>
  );

  const dateField = (
    <Controller
      name="date"
      control={control}
      render={({ field }) => (
        <DatePicker
          label="Date"
          value={pickerDate}
          onChange={(value: Date | null) => {
            setPickerDate(value);
            field.onChange(fromPickerDate(value));
          }}
          minDate={minDate ?? undefined}
          maxDate={maxDate ?? undefined}
          inputFormat="MMM d, yyyy"
          renderInput={(params) => (
            <TextField {...params} size="small" fullWidth />
          )}
        />
      )}
    />
  );

  const daysOfWeekField = (
    <Controller
      name="daysOfWeek"
      control={control}
      render={({ field }) => (
        <Box
          role="group"
          aria-label="Days of the week"
          sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}
        >
          {DAYS_OF_WEEK.map(({ value, label }) => {
            const selected = field.value.includes(value);
            return (
              <Chip
                key={value}
                label={label}
                clickable
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                aria-pressed={selected}
                // Selected days take the checked radio's colour above them.
                sx={(theme) =>
                  selected
                    ? {
                        color: theme.palette.common.white,
                        backgroundColor: theme.palette.primary[125],
                        "&:hover": {
                          backgroundColor: theme.palette.primary[125],
                        },
                      }
                    : {
                        color: theme.palette.grey[80],
                        borderColor: theme.palette.grey[80],
                        backgroundColor: theme.palette.common.white,
                      }
                }
                onClick={() =>
                  field.onChange(
                    selected
                      ? field.value.filter((day) => day !== value)
                      : [...field.value, value]
                  )
                }
              />
            );
          })}
        </Box>
      )}
    />
  );

  const timeZoneField = (
    <Controller
      name="timeZone"
      control={control}
      render={({ field }) => (
        <Autocomplete
          size="small"
          disableClearable
          options={timeZoneOptions}
          value={field.value}
          onChange={(_, value) => field.onChange(value)}
          getOptionLabel={timeZoneLabel}
          filterOptions={filterTimeZones}
          renderInput={(params) => <TextField {...params} label="Time zone" />}
        />
      )}
    />
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit}
        style={{
          height: "100%",
        }}
      >
        <Box
          sx={{
            py: 4,
            px: 6,
            display: "flex",
            flexDirection: "column",
            width: { xs: "400px", xl: "25vw" },
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Clock
                sx={{
                  color: "yellow.150",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h2">Time Delay</Typography>
              </Box>
            </Box>
            <IconButton sx={{ px: 0 }} onClick={goBack}>
              <CloseBig />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 5,
              mt: 6,
              overflowY: "auto",
            }}
          >
            <Controller
              name="mode"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  aria-label="Delay type"
                >
                  {DELAY_MODES.map(({ value, label }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio size="small" />}
                      label={<Typography variant="p2">{label}</Typography>}
                    />
                  ))}
                </RadioGroup>
              )}
            />
            {mode === "RELATIVE" ? (
              relativeFields
            ) : (
              <>
                {mode === "DAY_OF_WEEK" && daysOfWeekField}
                {mode === "SPECIFIC_DATE" && dateField}
                {timeFields}
                {timeZoneField}
                <Typography variant="p3" color="grey.80">
                  {hint[mode]}
                </Typography>
                {dateNote && (
                  <DelayDateNote severity={dateNote.severity}>
                    {dateNote.message}
                  </DelayDateNote>
                )}
              </>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Button onClick={goBack}>Cancel</Button>
            <Button
              variant="contained"
              type="submit"
              disabled={saveTimerDisabled || viewMode}
            >
              Save
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default TimeDelayForm;
